import { motion } from "framer-motion";
import { Music2 } from "lucide-react";
import SpotifyIcon from "../../../assets/Spotify_Icon_RGB_Green.png";
import AppleMusicIcon from "../../../assets/appleMusic.svg";
import YouTubeMusicIcon from "../../../assets/youtube-music-icon.png";
import YouTubeIcon from "../../../assets/youtube_social_icon_red.png";
import TidalIcon from "../../../assets/tidal_logo_black.png";
import { BRANDS, type ServiceKey } from "../pairGuideBrands";

const SERVICE_ICONS: Record<ServiceKey, string> = {
    spotify: SpotifyIcon,
    appleMusic: AppleMusicIcon,
    youtubeMusic: YouTubeMusicIcon,
    youtube: YouTubeIcon,
    tidal: TidalIcon,
};

type Mode = "transfer" | "sync";

type Props = {
    sourceKey: ServiceKey;
    destKey: ServiceKey;
    mode: Mode;
};

/**
 * Decorative hero visual: two service marks with an animated flow between them.
 */
export function ServicePairFlowVisual({ sourceKey, destKey, mode }: Props) {
    const source = BRANDS[sourceKey];
    const dest = BRANDS[destKey];
    const same = sourceKey === destKey;

    return (
        <div
            className="relative mx-auto mt-10 max-w-xl overflow-hidden rounded-3xl border border-zinc-200 bg-gradient-to-b from-zinc-50 to-white px-6 py-8 shadow-lg dark:border-zinc-700 dark:from-zinc-900/80 dark:to-zinc-950"
            aria-hidden
        >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-500/10 via-transparent to-transparent dark:from-purple-500/15" />

            <div className="relative flex flex-col items-stretch gap-6 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <motion.div
                    className="flex flex-col items-center gap-2 sm:flex-1"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white shadow-md ring-2 ring-zinc-200/80 dark:bg-zinc-800 dark:ring-zinc-600">
                        <img src={SERVICE_ICONS[sourceKey]} alt="" className="max-h-14 w-auto object-contain" />
                    </div>
                    <span className={`text-center text-sm font-semibold ${source.spanClass}`}>{source.name}</span>
                    <span className="text-center text-xs text-zinc-500 dark:text-zinc-400">Source</span>
                </motion.div>

                <div className="relative flex h-28 min-h-[7rem] w-full flex-1 flex-col items-center justify-center sm:min-w-[160px]">
                    <div className="pointer-events-none absolute left-[10%] right-[10%] top-1/2 h-1 -translate-y-1/2 rounded-full bg-gradient-to-r from-zinc-200 via-purple-200 to-zinc-200 dark:from-zinc-700 dark:via-purple-900/60 dark:to-zinc-700" />
                    <motion.div
                        className="pointer-events-none absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-purple-400/70 blur-[6px] dark:bg-purple-300/50"
                        animate={
                            mode === "sync"
                                ? { left: ["12%", "88%", "88%", "12%"], opacity: [0.4, 0.95, 0.95, 0.4] }
                                : { left: ["12%", "88%"], opacity: [0.5, 0.9] }
                        }
                        transition={
                            mode === "sync"
                                ? { duration: 3.2, repeat: Infinity, ease: "easeInOut" }
                                : { duration: 2.2, repeat: Infinity, repeatDelay: 0.35, ease: "easeInOut" }
                        }
                    />

                    <motion.div
                        className="absolute top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-purple-600 text-white shadow-lg ring-4 ring-purple-200/80 dark:bg-purple-500 dark:ring-purple-900/50"
                        style={{ marginLeft: "-1.5rem" }}
                        animate={
                            mode === "sync"
                                ? { left: ["14%", "86%", "86%", "14%"], scale: [1, 1.06, 1.06, 1] }
                                : { left: ["14%", "86%"], scale: [1, 1.05, 1] }
                        }
                        transition={
                            mode === "sync"
                                ? { duration: 3.2, repeat: Infinity, ease: "easeInOut" }
                                : { duration: 2.2, repeat: Infinity, repeatDelay: 0.35, ease: "easeInOut" }
                        }
                    >
                        <Music2 className="h-6 w-6" strokeWidth={2} aria-hidden />
                    </motion.div>

                    {mode === "sync" && !same && (
                        <motion.span
                            className="absolute bottom-1 z-[5] rounded-full bg-purple-100/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-purple-700 dark:bg-purple-950/80 dark:text-purple-200"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                        >
                            Two-way
                        </motion.span>
                    )}
                </div>

                <motion.div
                    className="flex flex-col items-center gap-2 sm:flex-1"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 }}
                >
                    <motion.div
                        className="flex h-24 w-24 items-center justify-center rounded-2xl bg-white shadow-md ring-2 ring-zinc-200/80 dark:bg-zinc-800 dark:ring-zinc-600"
                        animate={{ boxShadow: ["0 8px 24px rgba(124,58,237,0.12)", "0 12px 32px rgba(124,58,237,0.22)", "0 8px 24px rgba(124,58,237,0.12)"] }}
                        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <img src={SERVICE_ICONS[destKey]} alt="" className="max-h-14 w-auto object-contain" />
                    </motion.div>
                    <span className={`text-center text-sm font-semibold ${dest.spanClass}`}>{dest.name}</span>
                    <span className="text-center text-xs text-zinc-500 dark:text-zinc-400">
                        {same ? "Same service" : "Destination"}
                    </span>
                </motion.div>
            </div>

        </div>
    );
}
